import { useCallback, useEffect, useState } from 'react';
import { adminHeaders } from '../../lib/adminAuth';
import { tx } from '../../lib/adminI18n';

// 設定 → リピータークーポン タブ   設計: .plan/repeater-coupon-impl-v1.md §5.3
//   API: GET/PUT /api/admin/coupon, PATCH /api/admin/coupon/active

type Status = 'NONE' | 'ACTIVE' | 'PAUSED' | 'NOT_STARTED' | 'EXPIRED';
interface Coupon {
  code: string; discount_value: number; valid_from: string | null; valid_until: string | null;
  is_active: number; updated_at: string | null; updated_by: string | null; note: string | null;
}
interface HistoryRow { code: string; discount_value: number; valid_from: string | null; valid_until: string | null; updated_at: string | null; }
interface Form { code: string; discount_rate: string; valid_from: string; valid_until: string; note: string; }

const EMPTY: Form = { code: '', discount_rate: '10', valid_from: '', valid_until: '', note: '' };
const STATUS_COLOR: Record<Status, string> = {
  NONE: '#888', ACTIVE: '#1a7f37', PAUSED: '#b54708', NOT_STARTED: '#1d4ed8', EXPIRED: '#b42318',
};
const d10 = (v: string | null) => (v ? String(v).slice(0, 10) : '');
// DB の CURRENT_TIMESTAMP は UTC 'YYYY-MM-DD HH:MM:SS' → JST 表示
function fmtJst(v: string | null): string {
  if (!v) return '-';
  const t = Date.parse(v.includes('T') ? v : v.replace(' ', 'T') + 'Z');
  if (isNaN(t)) return v;
  return new Date(t + 9 * 3600e3).toISOString().slice(0, 16).replace('T', ' ');
}

export default function CouponSettings() {
  const [loading, setLoading] = useState(true);
  const [coupon, setCoupon] = useState<Coupon | null>(null);
  const [status, setStatus] = useState<Status>('NONE');
  const [usage, setUsage] = useState(0);
  const [maxRate, setMaxRate] = useState(30);
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [form, setForm] = useState<Form>(EMPTY);
  const [saved, setSaved] = useState<Form>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/admin/coupon', { headers: adminHeaders(), credentials: 'include' });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setMsg({ ok: false, text: tx('cp.loadErr') }); return; }
      const c: Coupon | null = d.coupon || null;
      setCoupon(c); setStatus(d.status || 'NONE'); setUsage(d.usage_count || 0);
      setMaxRate(d.max_rate || 30); setHistory(d.history || []);
      const f: Form = c
        ? { code: c.code, discount_rate: String(c.discount_value), valid_from: d10(c.valid_from), valid_until: d10(c.valid_until), note: c.note || '' }
        : EMPTY;
      setForm(f); setSaved(f);
    } catch {
      setMsg({ ok: false, text: tx('cp.loadErr') });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const dirty = JSON.stringify(form) !== JSON.stringify(saved);
  const set = (k: keyof Form, v: string) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    const code = form.code.trim().toUpperCase();
    const replacing = !!coupon && coupon.code !== code && Number(coupon.is_active) === 1;
    if (replacing && !window.confirm(tx('cp.confirmReplace', { s: coupon!.code }))) return;
    setBusy(true); setMsg(null);
    try {
      const res = await fetch('/api/admin/coupon', {
        method: 'PUT',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        credentials: 'include',
        body: JSON.stringify({
          code, discount_rate: Number(form.discount_rate),
          valid_from: form.valid_from, valid_until: form.valid_until,
          note: form.note, is_active: coupon ? Number(coupon.is_active) === 1 || replacing || coupon.code !== code : true,
          confirm_replace: replacing,
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setMsg({ ok: false, text: d.error || tx('cp.saveErr') }); return; }
      setMsg({ ok: true, text: tx('cp.saved') });
      await load();
    } catch (e: any) {
      setMsg({ ok: false, text: e?.message || tx('cp.saveErr') });
    } finally { setBusy(false); }
  };

  const toggle = async () => {
    if (!coupon) return;
    const next = Number(coupon.is_active) !== 1;
    if (!next && !window.confirm(tx('cp.confirmPause', { s: coupon.code }))) return;
    setBusy(true); setMsg(null);
    try {
      const res = await fetch('/api/admin/coupon/active', {
        method: 'PATCH',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        credentials: 'include',
        body: JSON.stringify({ is_active: next }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setMsg({ ok: false, text: d.error || tx('cp.saveErr') }); return; }
      setMsg({ ok: true, text: tx('cp.saved') });
      await load();
    } finally { setBusy(false); }
  };

  const guestText = coupon
    ? `以前はStay Dreamをご利用いただき、ありがとうございました。\n次回のご宿泊では、公式予約サイトでリピーター専用クーポンをご利用いただけます。\n\nクーポンコード：${coupon.code}\n割引：宿泊料金の${coupon.discount_value}%（清掃料金・税金は対象外）\n有効期限：${d10(coupon.valid_until)} までのご予約\n対象施設：Stay Dream Yotsugi / Flower Tea / GreatView\n\nご予約：https://booking.staydreamhotel.com/`
    : '';
  const copy = async () => {
    try { await navigator.clipboard.writeText(guestText); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* noop */ }
  };

  if (loading) return <p className="set-loading">{tx('set.31')}</p>;

  return (
    <>
      <section className="set-section">
        <h2 className="set-section-title">{tx('cp.tab')}</h2>
        <p className="set-hint" style={{ display: 'block', marginBottom: 12 }}>{tx('cp.desc')}</p>

        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
          <span>{tx('cp.status')}:</span>
          <span style={{ color: '#fff', background: STATUS_COLOR[status], borderRadius: 12, padding: '2px 10px', fontSize: 13 }}>
            {tx(`cp.st.${status}` as any)}
          </span>
          {coupon && <small className="set-hint">{tx('cp.usage', { n: usage })}</small>}
          {coupon && <small className="set-hint">{tx('cp.updated', { t: fmtJst(coupon.updated_at), by: coupon.updated_by || '-' })}</small>}
        </div>

        <div className="set-grid">
          <div className="set-group">
            <label className="set-label" htmlFor="cp-code">{tx('cp.code')}</label>
            <input id="cp-code" className="set-input" value={form.code} placeholder="WELCOME10"
              maxLength={30} autoComplete="off"
              onChange={(e) => set('code', e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))} />
            <small className="set-hint">{tx('cp.codeHint')}</small>
          </div>
          <div className="set-group">
            <label className="set-label" htmlFor="cp-rate">{tx('cp.rate')}</label>
            <input id="cp-rate" className="set-input" type="number" min={1} max={maxRate} step={1}
              value={form.discount_rate} onChange={(e) => set('discount_rate', e.target.value)} />
            <small className="set-hint">{tx('cp.rateHint', { n: maxRate })}</small>
          </div>
          <div className="set-group">
            <label className="set-label" htmlFor="cp-from">{tx('cp.from')}</label>
            <input id="cp-from" className="set-input" type="date" value={form.valid_from} onChange={(e) => set('valid_from', e.target.value)} />
          </div>
          <div className="set-group">
            <label className="set-label" htmlFor="cp-until">{tx('cp.until')}</label>
            <input id="cp-until" className="set-input" type="date" value={form.valid_until} min={form.valid_from || undefined}
              onChange={(e) => set('valid_until', e.target.value)} />
            <small className="set-hint">{tx('cp.periodHint')}</small>
          </div>
          <div className="set-group">
            <label className="set-label" htmlFor="cp-note">{tx('cp.note')}</label>
            <input id="cp-note" className="set-input" value={form.note} maxLength={500} onChange={(e) => set('note', e.target.value)} />
          </div>
        </div>

        {msg && <div className={msg.ok ? 'set-msg set-msg-ok' : 'set-msg set-msg-err'}>{msg.text}</div>}

        <div className="set-actions">
          {coupon && (
            <button className="set-btn set-btn-ghost" disabled={busy || dirty} onClick={toggle}>
              {Number(coupon.is_active) === 1 ? tx('cp.pause') : tx('cp.resume')}
            </button>
          )}
          <button className="set-btn set-btn-ghost" disabled={!dirty || busy} onClick={() => { setForm(saved); setMsg(null); }}>
            {tx('cp.reset')}
          </button>
          <button className="set-btn set-btn-primary"
            disabled={!dirty || busy || !form.code || !form.valid_from || !form.valid_until} onClick={save}>
            {busy ? tx('cp.saving') : tx('cp.save')}
          </button>
        </div>
      </section>

      {coupon && (
        <section className="set-section">
          <h2 className="set-section-title">{tx('cp.guestText')}</h2>
          <textarea className="set-input" readOnly rows={9} value={guestText} style={{ width: '100%', fontFamily: 'inherit' }} />
          <div className="set-actions">
            <button className="set-btn set-btn-ghost" onClick={copy}>{copied ? tx('cp.copied') : tx('cp.copy')}</button>
          </div>
        </section>
      )}

      <section className="set-section">
        <h2 className="set-section-title">{tx('cp.history')}</h2>
        {history.length === 0 ? <p className="set-hint">{tx('cp.noHistory')}</p> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <tbody>
              {history.map((h) => (
                <tr key={h.code} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '6px 4px', fontFamily: 'monospace' }}>{h.code}</td>
                  <td style={{ padding: '6px 4px' }}>{h.discount_value}%</td>
                  <td style={{ padding: '6px 4px' }}>{d10(h.valid_from)} 〜 {d10(h.valid_until)}</td>
                  <td style={{ padding: '6px 4px', color: '#888' }}>{fmtJst(h.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}
